<script setup>
import { useDetailsReport } from '#/detailsReport/useDetailsReport'
import { useFilters } from '#/general/useFilters'
import { computed, inject } from 'vue'
import PotentialVolumeProgressLine from '#/analyzer/PotentialVolumeProgressLine'
import RenovationPressureProgressLine from '#/analyzer/RenovationPressureProgressLine'
import AppMenu from '#/general/AppMenu'
import TooltipContainer from '#/general/TooltipContainer'

const { global, pages, screen } = inject('Shared')
const { potential } = useDetailsReport()
const { prettifyNumber } = useFilters()
const volume = computed(() => {
  return potential.value.parcel.currentVolume ? potential.value.parcel.currentVolume : 524
})

const renovationPressureLabel = computed(() => {
  const val = potential.value.parcel.renovationPressure * 100
  switch (true) {
    case (val < 20): return 'tiefen'
    case (val < 50 && val >= 20): return 'mittleren'
    case (val >= 50): return 'hohen'
  }
  return 'tiefen'
})

</script>

<template lang="pug">
.leadScoringContainer
  .infoBox
    .box
      .box-label--container
        .box--label Entwicklungspotenzial
        app-menu(position="fit" :content-width="screen.gt.sm ? '436px' : '300px'" tooltip-mode attach-to-parent :offset="screen.gt.sm ? [0, 0] : [-200, 0]")
          template(#parent)
            .box--label-icon
              svg(width='14' height='14' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg')
                circle(cx='11' cy='11' r='9.4' fill='#EB604F' stroke='white' stroke-width='1.2')
                path(d='M9.6 16V9.436H12.312V16H9.6ZM10.956 8.908C10.46 8.908 10.064 8.776 9.768 8.512C9.472 8.248 9.324 7.92 9.324 7.528C9.324 7.136 9.472 6.808 9.768 6.544C10.064 6.28 10.46 6.148 10.956 6.148C11.452 6.148 11.848 6.272 12.144 6.52C12.44 6.768 12.588 7.088 12.588 7.48C12.588 7.896 12.44 8.24 12.144 8.512C11.848 8.776 11.452 8.908 10.956 8.908Z' fill='white')
          template(#content)
            tooltip-container(title="Entwicklungspotenzial")
              span Das Entwickungspotenzial stellt die Erstabschätzung der ungenutzten Geschossflächen durch den Raumpioniere Algorithmus dar. Unser Algorithmus basiert auf öffentlich-rechtlichen Daten mit unter der kommunalen Bauordnung, dem
                a(style="margin-left: 3px" target='_blank' href='https://www.housing-stat.ch/de/start.html') Gebäude- und Wohnungsregister
                |  (GWR),
                a(style="margin-left: 3px" target='_blank' href='https://www.swisstopo.admin.ch/de/home.html') Swisstopo
                |               und
                a(style="margin-left: 3px" target='_blank' href='https://www.swissenergyplanning.ch/') Swiss Energy Planning
                |  (SEP) by
                a(style="margin-left: 3px" target='_blank' href='https://www.geoimpact.ch/') geoimpact AG.
      .secondaryText.row
        template(v-if="potential.analysis.grossFloorAreaPotential >= 0")
          span Die abgefragte Liegenschaft verfügt über ein Potenzial von ungefähr
            span.blue
              span.blueValue {{Math.round(potential.analysis.grossFloorAreaPotential)}}m
                span.smallText 2
              span.labelTooltipContainer
                span.labelTooltipTitle Geschossfläche.
                app-menu(position="fit" content-width="250px" tooltip-mode)
                  template(#parent)
                    .labelTooltipIcon
                      svg(width='14' height='14' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg')
                        circle(cx='11' cy='11' r='9.4' fill='#EB604F' stroke='white' stroke-width='1.2')
                        path(d='M9.6 16V9.436H12.312V16H9.6ZM10.956 8.908C10.46 8.908 10.064 8.776 9.768 8.512C9.472 8.248 9.324 7.92 9.324 7.528C9.324 7.136 9.472 6.808 9.768 6.544C10.064 6.28 10.46 6.148 10.956 6.148C11.452 6.148 11.848 6.272 12.144 6.52C12.44 6.768 12.588 7.088 12.588 7.48C12.588 7.896 12.44 8.24 12.144 8.512C11.848 8.776 11.452 8.908 10.956 8.908Z' fill='white')
                  template(#content)
                    tooltip-container(title="Geschossfläche" body="Die Geschossfläche ist die allseitig umschlossene und überdeckte Grundrissfläche aller zugänglichen Geschosse einschliesslich der Konstruktionsflächen.")
        div(v-else) Die abgefragte Liegenschaft ist möglicherweise übernutzt.
      .progress-line--container
        potential-volume-progress-line(
          width="386px"
          height="36px"
          smallTextFontSize="12px"
          :volume="potential.parcel.currentVolume"
          :potential-volume='potential.analysis ? potential.analysis.potentialVolume : 0'
        )
      .helperContainer.gt-sm
        .helperSign
          .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundLow}`")
          .label tief
        .helperSign
          .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundMedium}`")
          .label mittel
        .helperSign
          .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundHigh}`")
          .label hoch
      .lt-md
        .helperContainer
          .helperSign
            .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundLow}`")
            .label tief
          .helperSign
            .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundMedium}`")
            .label mittel
          .helperSign
            .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundHigh}`")
            .label hoch
        .separator
        .box-label--container
          .box--label Sanierungsdruck
          app-menu(position="fit" :content-width="screen.gt.sm ? '436px' : '300px'" tooltip-mode attach-to-parent :offset="screen.gt.sm ? [0, 0] : [-150, 0]")
            template(#parent)
              .box--label-icon
                svg(width='14' height='14' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg')
                  circle(cx='11' cy='11' r='9.4' fill='#EB604F' stroke='white' stroke-width='1.2')
                  path(d='M9.6 16V9.436H12.312V16H9.6ZM10.956 8.908C10.46 8.908 10.064 8.776 9.768 8.512C9.472 8.248 9.324 7.92 9.324 7.528C9.324 7.136 9.472 6.808 9.768 6.544C10.064 6.28 10.46 6.148 10.956 6.148C11.452 6.148 11.848 6.272 12.144 6.52C12.44 6.768 12.588 7.088 12.588 7.48C12.588 7.896 12.44 8.24 12.144 8.512C11.848 8.776 11.452 8.908 10.956 8.908Z' fill='white')
            template(#content)
              tooltip-container(title="Sanierungsdruck")
                span Der Sanierungsdruck ermittelt auf Basis des Gebäudezustands sowie der Sanierungsdynamik im näheren Umfeld die Notwendigkeit einer einer Optimierung oder Sanierung der Liegenschaft. Er basiert auf öffentlich-rechtlichen Daten mit unter
                  a(style="margin-left: 3px" target='_blank' href='https://www.housing-stat.ch/de/start.html') Gebäude- und Wohnungsregister
                  |  (GWR),
                  a(style="margin-left: 3px" target='_blank' href='https://www.swisstopo.admin.ch/de/home.html') Swisstopo
                  |               und
                  a(style="margin-left: 3px" target='_blank' href='https://www.swissenergyplanning.ch/') Swiss Energy Planning
                  |  (SEP) by
                  a(style="margin-left: 3px" target='_blank' href='https://www.geoimpact.ch/') geoimpact AG.
        .secondaryText
          span Die abgefragte Liegenschaft verfügt über einen
          span.blue {{renovationPressureLabel}} Sanierungsdruck.
        .progress-line--container.renovation-pressure-line--container
          renovation-pressure-progress-line(
            :renovation-pressure='potential.parcel.renovationPressure'
            height="30px"
            width="386px"
          )
        .helperContainer
          .helperSign
            .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundHigh}`")
            .label tief
          .helperSign
            .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundMedium}`")
            .label mittel
          .helperSign
            .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundLow}`")
            .label hoch
  .infoBox.gt-sm
    .box
      .box-label--container
        .box--label Sanierungsdruck
        app-menu(position="fit" content-width="436px" tooltip-mode attach-to-parent)
          template(#parent)
            .box--label-icon
              svg(width='14' height='14' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg')
                circle(cx='11' cy='11' r='9.4' fill='#EB604F' stroke='white' stroke-width='1.2')
                path(d='M9.6 16V9.436H12.312V16H9.6ZM10.956 8.908C10.46 8.908 10.064 8.776 9.768 8.512C9.472 8.248 9.324 7.92 9.324 7.528C9.324 7.136 9.472 6.808 9.768 6.544C10.064 6.28 10.46 6.148 10.956 6.148C11.452 6.148 11.848 6.272 12.144 6.52C12.44 6.768 12.588 7.088 12.588 7.48C12.588 7.896 12.44 8.24 12.144 8.512C11.848 8.776 11.452 8.908 10.956 8.908Z' fill='white')
          template(#content)
            tooltip-container(title="Sanierungsdruck")
              span Der Sanierungsdruck ermittelt auf Basis des Gebäudezustands sowie der Sanierungsdynamik im näheren Umfeld die Notwendigkeit einer einer Optimierung oder Sanierung der Liegenschaft. Er basiert auf öffentlich-rechtlichen Daten mit unter
                a(style="margin-left: 3px" target='_blank' href='https://www.housing-stat.ch/de/start.html') Gebäude- und Wohnungsregister
                |  (GWR),
                a(style="margin-left: 3px" target='_blank' href='https://www.swisstopo.admin.ch/de/home.html') Swisstopo
                |               und
                a(style="margin-left: 3px" target='_blank' href='https://www.swissenergyplanning.ch/') Swiss Energy Planning
                |  (SEP) by
                a(style="margin-left: 3px" target='_blank' href='https://www.geoimpact.ch/') geoimpact AG.
      .secondaryText
        span Die abgefragte Liegenschaft verfügt über einen
        span.blue {{renovationPressureLabel}} Sanierungsdruck.
      .progress-line--container
        renovation-pressure-progress-line(:renovation-pressure='potential.parcel.renovationPressure' height="30px" width="386px")
      .helperContainer.extraPadding
        .helperSign
          .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundHigh}`")
          .label tief
        .helperSign
          .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundMedium}`")
          .label mittel
        .helperSign
          .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundLow}`")
          .label hoch
</template>

<style scoped lang="sass">
.leadScoringContainer
  display: flex
  flex-direction: row
  width: 100%
  justify-content: space-between
  flex-wrap: wrap
  .infoBox
    @media screen and (min-width: 1024px)
      width: calc(50% - 15px)
    @media screen and (max-width: 1023px)
      width: 100%
      margin-bottom: 15px
    .box
      position: relative
      background: rgba(255, 255, 255, 0.7)
      border: 1px solid #D9D9D9
      box-sizing: border-box
      box-shadow: 0px 15px 30px rgba(166, 166, 166, 0.25)
      border-radius: 10px
      @media screen and (min-width: 1024px)
        padding: 20px
        height: 328px
      @media screen and (max-width: 1023px)
        padding: 15px
      .progress-line--container
        width: 100%
        display: flex
        justify-content: center
        align-items: center
        @media screen and (min-width: 1024px)
          margin: 40px 0 50px
        @media screen and (max-width: 1023px)
          margin: 15px 0
      .renovation-pressure-line--container
        margin-bottom: 30px !important
      .box-label--container
        display: flex
        flex-direction: row
        align-items: center
        @media screen and (min-width: 1024px)
          margin: 10px 0 28px 0
        @media screen and (max-width: 1023px)
          margin: 15px 0
        .box--label-icon
          margin-left: 10px
          display: flex
          justify-content: center
          align-items: center
      .box--label
        font-style: normal
        font-weight: bold
        text-transform: uppercase
        color: #626C85
        @media screen and (min-width: 1024px)
          line-height: 130%
          font-size: 16px
        @media screen and (max-width: 1023px)
          font-size: 14px
      .separator
        border: 1px solid #DCE7F2
        width: 100%
        margin: 15px 0 15px
      .secondaryText
        line-height: 140%
        color: #626C85
        @media screen and (min-width: 1024px)
          font-weight: 700
          font-size: 16px
        @media screen and (max-width: 1023px)
          font-weight: 500
          font-size: 14px
        .blue
          color: #007BFF
          margin-left: 4px
          position: relative
          .labelTooltipContainer
            position: relative
            .labelTooltipTitle
              padding-right: 10px
            .labelTooltipIcon
              position: absolute
              top: 0px
              right: -10px
          .blueValue
            position: relative
            margin-right: 10px
            .smallText
              position: absolute
              top: -4px
              right: -7px
              font-size: 10px
    .extraPadding
      padding-top: 32px
    .helperContainer
      display: flex
      flex-direction: row
      width: 100%
      justify-content: center
      align-items: center
      .helperSign
        display: flex
        flex-direction: row
        align-items: center
        margin-right: 23px
        @media screen and (min-width: 1024px)
          margin-bottom: 12px
        @media screen and (max-width: 1023px)
        .label
          font-weight: 500
          font-size: 14px
          line-height: 130%
          color: #626C85
        .circle
          width: 16px
          height: 16px
          border-radius: 100%
          margin-right: 6px
</style>
